// @coverage-include-exempt: thin hook over useExperiment (a latch plus a
// timer). Its behavior (control fallback after 1.5s, no late flip) is asserted
// through tests/component/monetization/apply-ad-break.test.tsx; vitest.config.ts
// is left alone because open PR #5956 edits it.
"use client";

import { useEffect, useState } from "react";
import { useExperiment } from "@/lib/analytics/use-experiment";
import { CLIENT_RESOLVE_TIMEOUT_MS } from "@/lib/net/fetch-with-timeout";

/**
 * `ad-format-experiment` (multivariate `control` / `ad_breaks`): does an extra
 * in-flow ad break in the post-apply success panel earn more than it costs in
 * engagement? The always-on units (sticky bar, in-list, mid-content, Hunt card)
 * are NOT part of this experiment; only the extra break is.
 *
 * The flag is the sole switch. Flag absent, PostHog blocked, or unresolved
 * after `CLIENT_RESOLVE_TIMEOUT_MS` all decide `control` (today's behaviour),
 * and the decision LATCHES: a flag that resolves late never flips a page that
 * already rendered its post-apply state.
 */
export const AD_FORMAT_EXPERIMENT_FLAG = "ad-format-experiment";

const VARIANTS = { control: false, ad_breaks: true } as const;

export type AdFormatArm = {
	/** False until the flag resolved or the bounded wait expired. */
	ready: boolean;
	arm: "control" | "ad_breaks";
	adBreaks: boolean;
};

export function useAdFormatArm(): AdFormatArm {
	// `trackExposure: false`: this hook mounts on every job page view, but only a
	// visitor who clicks Apply is ever exposed to a difference. The exposure
	// event is fired at that moment by `ApplyAdBreak` instead, so the arms are
	// compared over the people who actually reached the apply moment.
	const { variantKey, isReady } = useExperiment<boolean>({
		flagKey: AD_FORMAT_EXPERIMENT_FLAG,
		variants: VARIANTS,
		trackExposure: false,
	});
	const [decided, setDecided] = useState<"control" | "ad_breaks" | null>(null);

	useEffect(() => {
		if (isReady) {
			setDecided(
				(prev) =>
					prev ?? (variantKey === "ad_breaks" ? "ad_breaks" : "control"),
			);
		}
	}, [isReady, variantKey]);

	useEffect(() => {
		const timer = setTimeout(
			() => setDecided((prev) => prev ?? "control"),
			CLIENT_RESOLVE_TIMEOUT_MS,
		);
		return () => clearTimeout(timer);
	}, []);

	return {
		ready: decided !== null,
		arm: decided ?? "control",
		adBreaks: decided === "ad_breaks",
	};
}
